import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { LIMITS } from 'shared'
import type { Item, SellersLightAPIResponse } from 'typings'
import { useFetch } from './useFetch'

type UseSellersReturn = Item[]

type UseSellersParams = {
  options?: Partial<UseQueryOptions<UseSellersReturn>>
}

const ALLOWED_SELLER_TYPES = new Set<
  SellersLightAPIResponse['items'][number]['sellerType']
>([1, 3])

export function useSellers(params: UseSellersParams) {
  const { options } = params
  const { fetcherWrapper } = useFetch()

  const fetchData = async (from: number, to: number) => {
    const url = new URL(
      `/api/proxy/seller-register/pvt/sellers/_light`,
      window.location.origin
    )

    url.search = new URLSearchParams({
      from: from.toString(),
      to: to.toString(),
    }).toString()

    return fetcherWrapper(url).then(async (response) => response.json())
  }

  /**
   * This API does not return all data by default, so we need to make multiple requests
   * The `to` param has a limit settled to 500
   * @param total Total sellers returned from the first request
   * @returns All the sellers from the API
   */
  const requestAllData = async (total: number) => {
    let to = LIMITS.MAX_SELLERS_RETURN
    const requests: Promise<SellersLightAPIResponse>[] = []
    const items: any[] = []

    while (to < total) {
      to = to + LIMITS.MAX_SELLERS_RETURN
      const from = to - LIMITS.MAX_SELLERS_RETURN
      requests.push(fetchData(from, to))
    }

    const results = await Promise.allSettled(requests)

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        items.push(...result.value.items)
      }
    })
    return items
  }

  const queryFn = async () => {
    const url = new URL(
      `/api/proxy/seller-register/pvt/sellers/_light`,
      window.location.origin
    )
    url.search = new URLSearchParams({
      to: LIMITS.MAX_SELLERS_RETURN.toString(),
    }).toString()

    return fetcherWrapper(url)
      .then(async (response) => {
        const res: SellersLightAPIResponse = await response.json()
        const { items, paging } = res
        const allItems = await requestAllData(paging.total)
        return [...items, ...allItems]
      })
      .then((items: SellersLightAPIResponse['items']) =>
        items
          .filter((seller) => ALLOWED_SELLER_TYPES.has(seller.sellerType))
          .map((seller) => ({
            id: seller.id,
            name: seller.name,
          }))
      )
  }

  return useQuery<UseSellersReturn>({
    queryKey: ['sellers'],
    queryFn: queryFn,
    ...options,
  })
}
